import React from 'react'
import type { NextPage, GetServerSideProps, GetServerSidePropsContext } from 'next'
import Head from 'next/head'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { ParsedUrlQuery } from 'querystring'
import {
  ClientJavascriptContentProps,
  MIN_DONATION_AMOUNT,
  SearchSelectOption,
  useCustomEventListener,
  type ActivityFeedProps,
  type BlockIdToProgramDesignations,
  type DonationProps,
  type ImpactProps,
  type LayoutProps,
  type ProgramDesignation,
  type ProgressMetricsProps,
  type ShareButtonProps,
  type SocialLinksProps,
} from '@classy/campaign-page-blocks'
import { CampaignPage, CampaignPageProps } from 'components/CampaignPage'
import { OpenGraphTags } from 'components/OpenGraphTags'
import { PAGE_TYPES, PageConfig } from 'models/pages'
import { getMockData, getMockPageConfig } from 'services/mock-data'
import {
  findCampaignPage,
  denormalizeLayoutWithBlocks,
  getCampaignData,
  getCampaignPagesData,
  getLegacyCampaignData,
  isCampaignInactive,
} from 'services/campaign'
import {
  addSocialLinksProps,
  buildSocialShareLinks,
  checkIsInstagramSharingEnabled,
  fetchSocialShareVideoConfiguration,
} from 'features/Block/social-links'
import { getOrganizationData, getOrganizationTags } from 'services/organization'
import { domainMaskRedirect, getDomainFromRequest, getPrimaryDomain } from 'services/domain'
import { getSanitizedOrganizationChannels } from 'services/analytics'
import { getCheckoutQueryParamsToForward } from 'features/Block/donation/getCheckoutQueryParamsToForward'
import { getPageMetaDescription, getPageTitle } from 'utils/head'
import { isDomainValid } from 'utils/isDomainValid'
import { resolveFaviconUrl } from 'utils/resolveFaviconUrl'
import { __ENV } from 'utils/environment'
import { addImpactBlockProps, getProgramDesignationsFromImpactBlocks } from 'features/Block/impact'
import { getBlocksMap } from 'features/Block/utils/getBlocksMap'
import { addDonationBlockProps } from 'features/Block/donation/donation'
import { Block } from 'features/Block/block.model'
import { logger } from 'utils/logger'
import {
  getCampaignProgramDesignations,
  getDesignationData,
  toProgramDesignation,
} from 'services/designations'
import { DesignationDto } from 'models/designations'
import { useIntelligentAskAmount } from 'hooks/useIntelligentAskAmount'
import { getFooter, addFooterBlockProps } from 'features/Block/footer/footer'
import { getPageHeader, addPageHeaderBlockProps } from 'features/Block/page-header/page-header'
import { addLayoutBlockProps } from 'features/Block/layout/layout'
import { addActivityFeedProps } from 'features/Block/activity-feed/activity-feed'
import { addProgressMetricsProps } from 'features/Block/progress-metrics/progress-metrics'
import { addShareButtonProps } from 'features/Block/share-button/share-button'
import { getOrganizationMetaFrapiIntegration } from 'services/organization/integrations'
import { toSearchSelectOptions } from 'features/Block/donation/useFetchProgramDesignations'
import { resolveCampaignLocale } from 'utils/resolveCampaignLocale'
import { trackCustomPageView } from 'services/analytics/events/customPageView'
import { trackPageViewEvent } from 'services/analytics/events/pageView'
import { useConsentManager } from 'services/transcend'
import { useClientHeadersData, ClientHeadersData } from 'hooks/useClientHeadersData'
import { useSsoMember } from 'hooks/useSsoMember'
import {
  trackAddToCartEvent,
  AnalyticsTrackAddToCartEvent,
} from 'services/analytics/events/addToCart'
import { EVENT } from 'models/event'
import { SsoSdk } from 'components/SsoSdk'
import { getCookieValue } from 'utils/getCookieValue'
import { useInternalAnalytics } from 'services/analytics'
import { APIv2VideoStatus } from 'models/social-video'
import {
  addClientJavascriptContentProps,
  fetchClientJavascript,
} from 'features/Block/client-javascript-content/client-javascript-content'

interface DonationPageProps extends CampaignPageProps {
  isAnalyticsLoaded?: boolean
}

interface DonationPageQuery extends ParsedUrlQuery {
  mock?: string
  delay?: string
  campaignId: string
  pageId: string
}

const DonationPage: NextPage<DonationPageProps> = ({
  pageData,
  sharedBlocks,
  theme,
  pageConfig,
  isAnalyticsLoaded,
  __ENV,
}) => {
  const [isSsoSdkLoaded, setIsSsoSdkLoaded] = React.useState(false)
  const member = useSsoMember(isSsoSdkLoaded)
  const currentUrl = `https://${pageConfig.currentDomain}${pageConfig.pagePathname}`

  useIntelligentAskAmount(pageConfig.campaignId, pageConfig.intelligentAskConfig)

  /**
   * Fetching the client's headers data client side, instead of getServerSideProps
   * to prevent caching the IP address and currency.
   */
  const clientHeadersData: ClientHeadersData = useClientHeadersData()

  const { mParticle, consentSynced } = useInternalAnalytics()

  // TODO: CL-47302: Remove check with mParticle feature flag
  React.useEffect(() => {
    if (__ENV?.isMParticleEnabled && mParticle && consentSynced) {
      // * MParticle PageView Test
      mParticle.logPageView('Page', {
        campaign_created_at: pageConfig.campaignCreatedAt,
        campaign_currency_code: pageConfig.campaignRawCurrencyCode,
        campaign_default_designation_id: pageConfig.defaultDesignationId,
        campaign_goal: pageConfig.campaignRawGoal,
        campaign_id: Number(pageConfig.campaignId),
        campaign_is_embedded: false,
        campaign_name: pageConfig.campaignName,
        campaign_type: 'studio',
        member_id: member?.id ?? '',
        organization_id: pageConfig.orgId,
        organization_name: pageConfig.orgName,
        page_title: pageConfig.pageTitle,
        page_url: pageConfig.pagePathname,
        page_referrer: document.referrer,
      })
    }
  }, [__ENV?.isMParticleEnabled, consentSynced, member, mParticle, pageConfig])

  // TODO: CL-47302: Remove check with mParticle feature flag
  React.useEffect(() => {
    if (__ENV?.isMParticleEnabled && mParticle && consentSynced && member?.id) {
      // * MParticle Identity Sync Test
      mParticle.Identity.login({
        userIdentities: {
          email: member?.email_address,
          other: String(member?.id),
          other3: getCookieValue('classy-session-id'),
        },
      })
    }
  }, [__ENV?.isMParticleEnabled, consentSynced, member, mParticle])

  // TODO: CL-47302: Deprecate and remove `useConsentManager`
  const { airgap } = useConsentManager()
  /**
   * Fire page view events if airgap, isAnalyticsLoaded, clientHeadersData.cfConnectingIp are
   * true and only if member !== undefined. If member === undefined, then useSsoMember has not
   * finished checking whether a member is logged in or not.
   */
  React.useEffect(() => {
    if (
      (airgap || consentSynced) &&
      isAnalyticsLoaded &&
      !clientHeadersData.isLoading &&
      member !== undefined
    ) {
      // * GA4 & Tealium custom page view event
      trackCustomPageView(pageConfig, member)
      // * Default page view event used by all other analytics services
      trackPageViewEvent({ member, client_ip: clientHeadersData.cfConnectingIp })
    }
  }, [
    airgap,
    clientHeadersData.cfConnectingIp,
    clientHeadersData.isLoading,
    consentSynced,
    isAnalyticsLoaded,
    member,
    pageConfig,
  ])

  /**
   *   Custom Event Listener for Facebook Pixel and Meta CAPI trackAddToCartEvent analytics
   */
  const addToCartEventData = useCustomEventListener<AnalyticsTrackAddToCartEvent | null>(
    EVENT.ANALYTICS_TRACK_ADD_TO_CART,
    null,
  )

  React.useEffect(() => {
    if (addToCartEventData && addToCartEventData.newCartItem) {
      const { newCartItem, cartCount } = addToCartEventData

      trackAddToCartEvent({
        newCartItem,
        cartCount,
        clientIpAddress: clientHeadersData.cfConnectingIp,
        member,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addToCartEventData])

  return (
    <>
      <Head>
        <link rel="icon" href={resolveFaviconUrl(pageConfig.orgDomainMaskFavicon)} />
        <title>{pageConfig.pageTitle}</title>
        <meta
          name="description"
          content={getPageMetaDescription(PAGE_TYPES.DONATION, pageConfig)}
        />
      </Head>
      <SsoSdk onReady={() => setIsSsoSdkLoaded(true)} pageConfig={pageConfig} __ENV={__ENV} />
      <OpenGraphTags
        facebookAppId={__ENV?.facebookAppId}
        facebookText={pageConfig.openGraphTagFacebookText || ''}
        facebookImageUrl={pageConfig.openGraphTagFacebookImageUrl || ''}
        orgName={pageConfig.orgName}
        currentUrl={currentUrl}
      />
      <CampaignPage
        __ENV={__ENV}
        theme={theme}
        pageData={pageData}
        sharedBlocks={sharedBlocks}
        pageConfig={pageConfig}
        member={member}
        clientHeadersData={clientHeadersData}
      />
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  const { locale, query, req, res, resolvedUrl } = context
  const { campaignId, delay = '0', mock, skipDomainMaskRedirect } = query as DonationPageQuery

  logger('trace', 'Start compiling donation page', {
    context: { campaignId, query, resolvedUrl },
  })

  try {
    if (mock) {
      const { theme, sharedBlocks, pageData } = await getMockData(
        Number(delay),
        campaignId,
        PAGE_TYPES.DONATION,
      )

      const { pageConfig } = await getMockPageConfig(campaignId, PAGE_TYPES.DONATION)

      return {
        props: {
          __ENV,
          theme,
          sharedBlocks,
          pageData,
          pageConfig,
        },
      }
    }

    /**
     * - Fetch the raw campaign data
     * - Fetch the donation page
     */
    const published = true
    const [campaign, campaignPages] = await Promise.all([
      getCampaignData({ campaignId, req, published }),
      getCampaignPagesData({ campaignId, req, published }),
    ])

    const [
      organization,
      domainMask,
      dataUseRestrictionTag,
      disableMulticurrencyTag,
      allowSpanishSpainLocalizationTag,
      allowDonationProgramDesignationsSearchTag,
      allowIntelligentAsksTag,
      allowVideoSharingTag,
      allowClientJavascriptContentTag,
    ] = await Promise.all([
      getOrganizationData({
        organizationId: campaign.organization_id,
        req,
      }),
      getPrimaryDomain({
        organizationId: campaign.organization_id,
        req,
      }),
      getOrganizationTags({
        organizationId: campaign.organization_id,
        filter: 'name=data-use-restriction',
        req,
      }),
      getOrganizationTags({
        organizationId: campaign.organization_id,
        filter: 'name=disable-multi-currency',
        req,
      }),
      getOrganizationTags({
        organizationId: campaign.organization_id,
        filter: 'name=localization-spanish_spain',
        req,
      }),
      getOrganizationTags({
        organizationId: campaign.organization_id,
        filter: 'name=donation-block-search',
        req,
      }),
      getOrganizationTags({
        organizationId: campaign.organization_id,
        filter: 'name=intelligent-asks',
        req,
      }),
      getOrganizationTags({
        organizationId: campaign.organization_id,
        filter: 'name=enable-video-sharing',
        req,
      }),
      getOrganizationTags({
        organizationId: campaign.organization_id,
        filter: 'name=client-javascript-content',
        req,
      }),
    ])

    const domain = getDomainFromRequest(req)
    const hostUrl = `https://${domain}`

    if (!isDomainValid(domain, domainMask)) {
      logger('error', new Error('Invalid domain or domain mask'), {
        context: { campaignId, query, domain, domainMask },
      })

      return {
        notFound: true,
      }
    }

    // When we implement Cloudflare caching on our pages, we will likely need to put in special rule
    // to specifically cache redirects (i.e. 308s) as Cloudflare does not cache certain redirects by
    // default: https://developers.cloudflare.com/cache/how-to/configure-cache-status-code
    if (!skipDomainMaskRedirect) {
      const redirect = domainMaskRedirect({
        currentDomain: domain,
        domainMask,
        url: resolvedUrl,
      })
      if (redirect) {
        return redirect
      }
    }

    if (isCampaignInactive(campaign)) {
      logger('warn', new Error('Campaign is inactive'), {
        context: {
          campaignId,
          externalUrl: campaign?.campaign_data?.external_url,
          organizationUrl: organization?.url,
        },
      })

      return {
        notFound: !campaign?.campaign_data?.external_url && !organization?.url,
        redirect: {
          permanent: false,
          destination: campaign?.campaign_data?.external_url || organization?.url,
        },
      }
    }

    const { theme, sharedBlocks } = campaignPages

    const donationPage = findCampaignPage(campaignPages, PAGE_TYPES.DONATION)
    if (!donationPage) {
      const error = new Error('Unable to find donation page in campaignPages data')
      logger('error', error, { context: { campaignId, campaignPages } })
      throw error
    }

    let donationPageData: Block
    try {
      donationPageData = denormalizeLayoutWithBlocks(donationPage)
    } catch (e) {
      const error = new Error('Unable to denormalize donation page data', { cause: e })
      logger('error', error, {
        context: { campaignId, blocks: donationPage.blocks, layout: donationPage.layout },
      })
      throw error
    }

    const getCheckoutBaseUrl = () => {
      if (domainMask) {
        return hostUrl
      }

      return `https://${process.env.CLASSY_STUDIO_DOMAIN}`
    }

    /**
     * Intelligent Ask Amount (IAA) setup
     *
     * TODO: CL-48068, clean up org tag, legacy IAA logic and merge onetimeEnabled and
     *       recurringEnabled into a single boolean.
     */

    // If an org has a data use restriction tag, make sure IAA is set to false
    const dataUseRestriction = dataUseRestrictionTag?.data?.length === 1

    let intelligentAskConfig: PageConfig['intelligentAskConfig']
    let isNewIaaEnabled = false
    if (allowIntelligentAsksTag?.data?.length === 1) {
      /**
       * New Intelligent Ask Amount
       *
       * For backwards compatibility with legacy IAA, the is_enabled value is translated
       * to the onetimeEnabled and recurringEnabled values.
       */
      isNewIaaEnabled = !dataUseRestriction && (campaign?.intelligent_asks?.is_enabled || false)
      intelligentAskConfig = {
        onetimeEnabled: isNewIaaEnabled,
        recurringEnabled: isNewIaaEnabled,
      }
    } else {
      /**
       * Legacy IAA
       *
       * Legacy behavior relies on campaign_data.use_intelligent_ask_recurring and
       * campaign_data.use_intelligent_ask_onetime. If either are enabled, fetch IAA amounts.
       *
       * Note, if one is enabled and one disabled, the response from IAA would look like:
       * { suggested_donation_amount: number, suggested_recurring_amount: null }
       */
      intelligentAskConfig = {
        onetimeEnabled:
          !dataUseRestriction && (campaign?.campaign_data?.use_intelligent_ask_onetime || false),
        recurringEnabled:
          !dataUseRestriction && (campaign?.campaign_data?.use_intelligent_ask_recurring || false),
      }
    }

    const [organizationChannels, organizationMetaFrapiIntegration] = await Promise.all([
      getSanitizedOrganizationChannels({
        organizationId: campaign.organization_id,
        req,
      }),
      getOrganizationMetaFrapiIntegration({
        organizationId: campaign.organization_id,
        req,
      }),
    ])

    /**
     * Whammy-specific runtime data (not injected into block data). All attributes MUST
     * have a value as any props returned by getServerSideProps will be serialized
     * and undefined values will throw an error.
     */
    const pageConfig: PageConfig = {
      analyticsServiceSettings: campaign?.modules?.analytics_service_settings ?? [],
      campaignId,
      campaignName: campaign.campaign_data.name,
      campaignCreatedAt: campaign.created_at,
      campaignLocale: resolveCampaignLocale(
        locale,
        campaign.default_locale,
        allowSpanishSpainLocalizationTag,
      ),
      campaignRawCurrencyCode: campaign.campaign_data.raw_currency_code,
      campaignRawGoal: campaign.campaign_data.raw_goal,
      defaultDesignationId: campaign.campaign_data.designation_id,
      checkoutBaseUrl: getCheckoutBaseUrl(),
      currentDomain: domain,
      forwardCheckoutQueryParams: getCheckoutQueryParamsToForward(query),
      isCartEnabled: campaign?.checkout?.cart?.is_enabled ?? false,
      isPassportDisabled: disableMulticurrencyTag?.data?.length === 1,
      openGraphTagFacebookText: campaign?.modules.sharing?.facebook_text || '',
      openGraphTagFacebookImageUrl: campaign?.modules.sharing?.facebook_image_url || '',
      organizationChannels: organizationChannels || [],
      orgDomainMaskFavicon: domainMask?.favicon ?? '',
      orgId: campaign.organization_id,
      orgName: organization.name,
      pagePathname: req.url ?? '',
      pageTitle: getPageTitle(PAGE_TYPES.DONATION, campaign.campaign_data.name),
      intelligentAskConfig,
    }

    // * Adjust shared blocks

    const pageHeaderBlock = getPageHeader(sharedBlocks)
    if (pageHeaderBlock) {
      const { isCartEnabled = false } = pageConfig

      addPageHeaderBlockProps(pageHeaderBlock.props, organization, isCartEnabled)
    }

    const footerBlock = getFooter(sharedBlocks)
    if (footerBlock) {
      addFooterBlockProps(footerBlock.props, organization, campaign)
    }

    // * Adjust non-shared blocks

    const blocksMap = getBlocksMap(donationPageData)

    if (blocksMap['activity-feed']) {
      // ! Assumes a campaign page will only have a single activity feed block
      const activityFeed = blocksMap['activity-feed'][0]
      addActivityFeedProps(
        // Force narrowing type (AnyBlockProps -> ActivityFeedProps)
        activityFeed.props as ActivityFeedProps,
        campaignId,
        campaign.organization_id,
        campaign?.modules?.donation_matching_plan,
      )
    }

    // TODO added per CL-39568, clean up with CL-40328
    blocksMap.layout?.forEach((layoutBlock) => {
      addLayoutBlockProps(layoutBlock.props as LayoutProps, campaignPages.schema_version)
    })

    if (blocksMap['progress-metrics']) {
      blocksMap['progress-metrics'].forEach((progressMetricsBlock) => {
        addProgressMetricsProps(
          progressMetricsBlock.props as ProgressMetricsProps,
          campaign.campaign_data.raw_currency_code,
        )
      })
    }

    if (blocksMap['social-links'] || blocksMap['share-button']) {
      let videoData: APIv2VideoStatus | undefined
      let videoPreviewOptions: SocialLinksProps['videoPreview']

      if (allowVideoSharingTag?.data?.length === 1) {
        const { videoData: fetchedVideoData, videoPreviewOptions: fetchedVideoPreviewOptions } =
          await fetchSocialShareVideoConfiguration(campaign, req)

        videoData = fetchedVideoData
        videoPreviewOptions = fetchedVideoPreviewOptions
      }

      // Build social share links with the video data
      const socialShareLinks = buildSocialShareLinks(campaign, domainMask?.value, videoData)

      const isInstagramSharingEnabled = checkIsInstagramSharingEnabled(
        organizationMetaFrapiIntegration,
        campaign.modules.campaign_channel || [],
      )

      blocksMap['social-links']?.forEach((socialLinksBlock) => {
        addSocialLinksProps(
          socialLinksBlock.props as SocialLinksProps,
          socialShareLinks,
          isInstagramSharingEnabled,
          videoPreviewOptions,
        )
      })

      blocksMap['share-button']?.forEach((shareButtonBlock) => {
        addShareButtonProps(
          shareButtonBlock.props as ShareButtonProps,
          socialShareLinks,
          isInstagramSharingEnabled,
          videoPreviewOptions,
        )
      })
    }

    let defaultProgramDesignationId: number | null = null
    let defaultProgramDesignation: ProgramDesignation = {} as ProgramDesignation
    let programDesignations: BlockIdToProgramDesignations = {}
    try {
      /**
       * Under Settings > Program Designations, the value for sort_designation_by is saved at the
       * legacy APIv2 /campaigns/{id} end point.
       *
       * https://docs.classy-test.org/apiv2/#tag/Campaign/operation/fetchCampaign
       *
       * Then fetch the campaign's sorted program designation group by sort_designation_by.
       * Assumes designation_id is the campaign's default program designation.
       */
      const { designation_id, sort_designation_by } = await getLegacyCampaignData({
        campaignId,
        req,
      })

      defaultProgramDesignationId = designation_id

      const designationData = await getDesignationData({
        designationId: designation_id,
        req,
      })

      const designationDataJson: DesignationDto = await designationData.json()
      defaultProgramDesignation = toProgramDesignation(designationDataJson)

      if (blocksMap.impact) {
        // Precompiling all designations across all impact blocks
        programDesignations = await getProgramDesignationsFromImpactBlocks(
          blocksMap.impact,
          sort_designation_by,
          defaultProgramDesignation,
          req,
          campaignId,
        )
      }
    } catch (e) {
      const error = new Error('Unable to apply program designations to blocks', { cause: e })
      logger('error', error, {
        context: { defaultProgramDesignationId, defaultProgramDesignation },
      })

      throw error
    }

    blocksMap.impact?.forEach((impactBlock) => {
      addImpactBlockProps(
        // Force narrowing type (AnyBlockProps -> ImpactProps)
        impactBlock.props as ImpactProps,
        campaignId,
        pageConfig.checkoutBaseUrl || '',
        pageConfig.forwardCheckoutQueryParams || {},
        programDesignations,
        campaign.checkout?.cart?.is_enabled || false,
        campaign.campaign_data.minimum_donation_amount || MIN_DONATION_AMOUNT,
        campaign.organization_id,
        campaign.campaign_data.raw_currency_code || 'USD',
      )
    })

    let initialCampaignProgramDesignations: SearchSelectOption[] = []
    if (
      allowDonationProgramDesignationsSearchTag?.data?.length === 1 &&
      blocksMap.donation &&
      pageConfig.isCartEnabled
    ) {
      const fetchDesignations = blocksMap.donation?.some(
        // Force narrowing type (AnyBlockProps -> DonationProps)
        (donationBlock) => (donationBlock.props as DonationProps)?.showProgramDesignationsSearch,
      )

      if (fetchDesignations) {
        const designations = await getCampaignProgramDesignations({ campaignId })
        initialCampaignProgramDesignations = toSearchSelectOptions(designations)
      }
    }

    blocksMap.donation?.forEach((donationBlock) => {
      /**
       * FYI, IAA config is not block data, it is external block data, which comes from
       * campaign.campaign_data, and passed to the client via PageConfig.
       */
      addDonationBlockProps(
        // Force narrowing type (AnyBlockProps -> DonationProps)
        donationBlock.props as DonationProps,
        query,
        campaignId,
        campaign.organization_id,
        campaign.campaign_data.dcf_enabled,
        defaultProgramDesignation,
        pageConfig.forwardCheckoutQueryParams || {},
        isNewIaaEnabled
          ? MIN_DONATION_AMOUNT
          : campaign?.campaign_data?.minimum_donation_amount || MIN_DONATION_AMOUNT,
        campaign?.checkout?.cart?.is_enabled ?? false,
        pageConfig.checkoutBaseUrl || '',
        campaign.campaign_data.raw_currency_code,
        initialCampaignProgramDesignations,
        pageConfig.intelligentAskConfig,
      )
    })

    if (allowClientJavascriptContentTag?.data?.length === 1) {
      /**
       * The `IframeContent` content block allows clients to embed a custom script via a
       * sandboxed iframe.
       *
       * ! We are experimenting with this block, which is why this does not exist on the
       * ! preview or thank you page yet.
       *
       * Todo:
       * - How to handle multiple js customizations block? Add a user-provided "id" block prop?
       *   - Note, using the block id right now since we are hardcoding the block. That won't work
       *     when Rich Mahogany gets involved.
       * - Better handling when the script can't be loaded.
       * - Loading state?
       * - Variable substitution?
       */

      logger('info', 'Processing ClientJavascriptContent block(s) on the donation page', {
        context: { campaignId },
      })

      for (const clientJavascriptContentBlock of blocksMap['client-javascript-content'] || []) {
        const script = await fetchClientJavascript(campaignId, clientJavascriptContentBlock.id)

        addClientJavascriptContentProps(
          clientJavascriptContentBlock.props as ClientJavascriptContentProps,
          campaignId,
          query,
          script,
        )
      }
    }

    // * Set CloudFlare cache-tag
    res.setHeader(
      'Cache-Tag',
      `campaign-${campaignId}-${PAGE_TYPES.DONATION},campaign-${campaignId},organization-${campaign.organization_id}`,
    )

    logger('trace', 'Successfully compiled donation page', {
      context: { campaignId },
    })

    return {
      props: {
        __ENV,
        theme,
        sharedBlocks,
        pageData: donationPageData,
        pageConfig,
        ...(await serverSideTranslations(pageConfig.campaignLocale)),
      },
    }
  } catch (e) {
    logger('error', new Error('Unable to compile donation page', { cause: e }), {
      context: { campaignId, query, resolvedUrl },
    })

    return {
      notFound: true,
    }
  }
}

export default DonationPage
